import { ISetupStep, StepStatus } from './types';

export const STATUS_LABEL: Record<StepStatus, string> = {
	pending: 'Pending',
	in_progress: 'In Progress',
	completed: 'Completed',
	error: 'Error',
	blocked: 'Blocked',
};

export const STATUS_ICON: Record<StepStatus, string> = {
	pending: 'fas fa-circle',
	in_progress: 'fas fa-spinner fa-spin',
	completed: 'fas fa-check',
	error: 'fas fa-times',
	blocked: 'fas fa-lock',
};

// Step statuses are mock-driven in phase 1.
// They will be replaced by real Community API data once
// the GET /community response includes oneSignal, supabase, and customDomain status fields.
export const buildMockSteps = (): ISetupStep[] => [
	{
		id: 'branch',
		label: 'Branch',
		sublabel: 'Project & App ID',
		icon: 'fas fa-code-branch',
		status: 'pending',
		description: 'Initialize a Branch project for the community and save the branch details to the database.',
		details: [
			'Create a new Branch account (or use an existing one) for the client.',
			'Obtain the Branch App ID of the new application being built for this client.',
			'Register the Branch project details — once saved, this step is locked.',
		],
	},
	{
		id: 'custom_domain',
		label: 'Custom Domain',
		sublabel: 'Required before Supabase & OneSignal',
		icon: 'fas fa-globe',
		status: 'pending',
		description: 'Enable a custom domain for this Network Plus community. A custom domain is mandatory before Supabase and OneSignal can be configured.',
		details: [
			'Provide the verified custom domain the client owns (e.g. app.client.com).',
			'DNS propagation may take up to 24–48 hours — confirm before proceeding.',
			'Supabase and OneSignal steps unlock only after this step is marked complete.',
		],
	},
	{
		id: 'supabase',
		label: 'Supabase',
		sublabel: 'Auth & Database',
		icon: 'fas fa-database',
		status: 'blocked',
		description: 'Configure Supabase for sign-in and authenticated user management on this Network Plus community.',
		details: [
			'Requires: custom domain must be active and verified first.',
			'Provision a new Supabase project tied to this community.',
			'Store project URL and anon/service keys against the community record.',
		],
	},
	{
		id: 'onesignal',
		label: 'OneSignal',
		sublabel: 'Push Notifications',
		icon: 'fas fa-bell',
		// Set to 'pending' so provisioning can be exercised now.
		// Will be set to 'blocked' or 'completed' from Community API in phase 2.
		status: 'pending',
		description: "Connect OneSignal to enable push notifications for authenticated users on this community's Network Plus app.",
		details: [
			'Provision a new OneSignal app directly from this panel — credentials are stored automatically.',
			'The returned App ID is tied to this community in the platform database.',
			'Provisioning is available only once per community while OneSignal is not yet active.',
		],
	},
	{
		id: 'posthog_analytics',
		label: 'PostHog Analytics',
		sublabel: 'API Key',
		icon: 'fas fa-chart-line',
		status: 'pending',
		description: 'Connect PostHog Analytics for this Network Plus community by saving the PostHog API key.',
		details: [
			'Add the PostHog API key for this community.',
			'This setup does not require a custom domain.',
			'Once saved, the analytics setup is locked and the stored key is shown for reference.',
		],
	},
	{
		id: 'mobile_app',
		label: 'Branded Mobile App',
		sublabel: 'Intake & store review',
		icon: 'fas fa-mobile-alt',
		status: 'pending',
		description:
			"Track this community's white-labeled iOS and Android app from intake through to the stores. Fulfilment is manual, so this panel is where progress is recorded.",
		details: [
			'Nothing is provisioned here — the app is built by hand and this records where it has got to.',
			'Payment gates development: use Mark as paid for customers invoiced offline.',
			'Apple and Google are tracked separately, because they routinely disagree.',
		],
	},
];
